import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';

// ============================================
// KONFIGURACJA STRIPE
// ============================================
// Zamień na swój publishable key
const stripePromise = loadStripe('pk_test_51RqGwMRhCrrvDMQST1bFZSnRAPXKn8PNdWjUuRZP1c7QLngwywi4yxFKEYItxKymDFPufpeqE07Ca2ODKMtGXQnq00YBquhRsV');

// ============================================
// GŁÓWNY KOMPONENT - WRAPPER
// ============================================
export default function PaymentV2() {
    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4">
            <div className="max-w-md mx-auto">
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">
                        Płatność
                    </h1>

                    <Elements stripe={stripePromise}>
                        <PaymentForm />
                    </Elements>
                </div>
            </div>
        </div>
    );
}

// ============================================
// FORMULARZ PŁATNOŚCI
// ============================================
function PaymentForm() {
    const stripe = useStripe();
    const elements = useElements();

    // State
    const [amount, setAmount] = useState('2400'); // w groszach (24.00 PLN)
    const [currency, setCurrency] = useState('pln');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // ============================================
    // OBSŁUGA PŁATNOŚCI
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            // KROK 1: Utwórz PaymentMethod
            console.log('🔐 Tworzenie PaymentMethod...');
            const cardElement = elements.getElement(CardElement);

            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: name,
                    email: email,
                },
            });

            if (pmError) {
                throw new Error(pmError.message);
            }

            console.log('✅ PaymentMethod utworzony:', paymentMethod.id);

            // KROK 2: Wyślij do backendu
            console.log('📤 Wysyłanie do backendu...');
            const response = await fetch('/api/stripe/create-payment-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: parseInt(amount),
                    currency: currency,
                    paymentMethodId: paymentMethod.id,
                    userId: 'user-123',
                }),
            });

            if (!response.ok) {
                throw new Error(`Backend error: ${response.status}`);
            }

            const data = await response.json();
            const clientSecret = data.clientSecret;

            if (!clientSecret) {
                throw new Error('Brak clientSecret w odpowiedzi');
            }

            console.log('🔑 Otrzymano clientSecret');

            // KROK 3: Potwierdź płatność
            console.log('💳 Potwierdzanie płatności...');
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: paymentMethod.id,
                }
            );

            if (confirmError) {
                throw new Error(confirmError.message);
            }

            console.log('✅ Płatność zakończona:', paymentIntent.status);

            // KROK 4: Sprawdź status
            if (paymentIntent.status === 'succeeded') {
                setSuccessMessage('✅ Płatność zakończona sukcesem!');
                setAmount('2400');
                setEmail('');
                setName('');
                cardElement.clear();
            } else if (paymentIntent.status === 'processing') {
                setSuccessMessage('⏳ Płatność w trakcie przetwarzania...');
            } else {
                setErrorMessage(`Nieoczekiwany status: ${paymentIntent.status}`);
            }

        } catch (error) {
            console.error('❌ Błąd:', error);
            setErrorMessage(error.message || 'Wystąpił błąd podczas płatności');
        } finally {
            setIsProcessing(false);
        }
    };

    // ============================================
    // STYLE DLA CARDELEMENT
    // ============================================
    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
                fontFamily: 'system-ui, -apple-system, sans-serif',
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className="space-y-4">
            {/* Kwota */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kwota (w groszach)
                </label>
                <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2400"
                    min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                    {(parseInt(amount) / 100).toFixed(2)} {currency.toUpperCase()}
                </p>
            </div>

            {/* Waluta */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Waluta
                </label>
                <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="pln">PLN</option>
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                    <option value="gbp">GBP</option>
                </select>
            </div>

            {/* Imię i nazwisko */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imię i nazwisko
                </label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Jan Kowalski"
                />
            </div>

            {/* Email */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                </label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="jan@example.com"
                />
            </div>

            {/* Karta */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dane karty
                </label>
                <div className="p-3 border border-gray-300 rounded-md">
                    <CardElement options={cardElementOptions} />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    Test: 4242 4242 4242 4242 | CVV: 123 | Data: 12/34
                </p>
            </div>

            {/* Komunikaty */}
            {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
            )}

            {successMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800">{successMessage}</p>
                </div>
            )}

            {/* Przycisk */}
            <button
                onClick={handleSubmit}
                disabled={!stripe || isProcessing}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                {isProcessing ? (
                    <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Przetwarzanie...
          </span>
                ) : (
                    `Zapłać ${(parseInt(amount) / 100).toFixed(2)} ${currency.toUpperCase()}`
                )}
            </button>

            {/* Info o bezpieczeństwie */}
            <p className="text-xs text-gray-500 text-center">
                🔒 Płatności są przetwarzane bezpiecznie przez Stripe.
                Twoje dane karty są szyfrowane.
            </p>
        </div>
    );
}